/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type request states.
 */
import { RequestState } from '@ngt/request-utilities';

/*
 * Used to show loading view.
 */
import { RouteLoading } from '@ngt/opms';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to pass patient context to the rest of the app.
 */
import ScreenFailuresContext from '../../contexts/data/ScreenFailuresContext';

/*
 * Used to load collaborating groups
 */
import useGetScreenFailures from '../../hooks/data/useGetScreenFailures';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

export interface IScreenFailuresResolverProps {
    resolveBeforeLoad?: boolean | null;
}

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const ScreenFailuresResolver: React.FunctionComponent<IScreenFailuresResolverProps> = ({
    resolveBeforeLoad,
    children
}) => {
    const [screenFailures, loadState, actions] = useGetScreenFailures(true);

    const context = React.useMemo(() => {
        return {
            screenFailures,
            loadState,
            actions
        };
    }, [screenFailures, loadState, actions]);

    React.useEffect(() => {
        if (context.screenFailures == null && loadState.state !== RequestState.Pending) {
            actions.load();
        }
    }, [context]);

    return (
        <ScreenFailuresContext.Provider value={context}>
            {
                !resolveBeforeLoad || (loadState.state !== RequestState.None && loadState.state !== RequestState.Pending) ?
                    children :
                    <RouteLoading />
            }
        </ScreenFailuresContext.Provider>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default ScreenFailuresResolver;